import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Tag, Button, Spin, Empty, message, Row, Col,
  Avatar, Collapse, Timeline, Progress, Input
} from "antd";
import {
  ArrowLeftOutlined, UserOutlined, TeamOutlined,
  ExclamationCircleOutlined, MessageOutlined, CheckCircleOutlined,
  StarFilled, FileTextOutlined, QuestionCircleOutlined, SendOutlined, DownloadOutlined
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import { useParams, useNavigate } from "react-router-dom";

const { Panel } = Collapse;
const { TextArea } = Input;

const Wrapper = styled.div`
  padding: 0;

  .contract-panel .ant-collapse-header {
    align-items: center !important;
    padding: 14px 20px !important;
  }

  .contract-panel .ant-collapse-content-box {
    padding: 0 20px 20px !important;
  }
`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const scoreColor = s => {
  if (!s && s !== 0) return "#aaa";
  if (s >= 80) return "#52c41a";
  if (s >= 50) return "#1677ff";
  if (s >= 30) return "#fa8c16";
  return "#ff4d4f";
};

const CONTRACT_STATUS = {
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "default" },
  terminated: { label: "Terminated", color: "error" },
  draft: { label: "Draft", color: "default" },
};

const TYPE_COLOR = { instant: "orange", monthly: "blue", contract_end: "purple" };
const TYPE_LABEL = { instant: "Instant", monthly: "Monthly Review", contract_end: "Contract End" };
const SUBTYPE_COLOR = { concern: "error", warning: "orange", behavioral_recognition: "success", general: "processing" };
const SUBTYPE_LABEL = { concern: "Concern", warning: "Warning", behavioral_recognition: "Recognition", general: "General" };

const feedbackTypeLabel = f => {
  if (f.feedbackType === "instant") return TYPE_LABEL.instant;
  if (f.feedbackType === "monthly") return `Monthly – ${MONTHS[(f.month || 1) - 1]} ${f.year}`;
  return "Contract End";
};

const statusTag = s => {
  if (s === "open") return <Tag color="error" style={{ borderRadius: 20, fontSize: 11 }}>Open</Tag>;
  if (s === "acknowledged_by_partner") return <Tag color="warning" style={{ borderRadius: 20, fontSize: 11 }}>Acknowledged</Tag>;
  return <Tag color="default" style={{ borderRadius: 20, fontSize: 11 }}>Closed</Tag>;
};

const ResponseBubble = ({ r }) => {
  const isCustomer = r.respondedByRole === "customer";
  const isAdmin = r.respondedByRole === "admin";
  return (
    <div style={{
      padding: "7px 12px",
      background: isCustomer ? "#f0fff4" : isAdmin ? "#fffbe6" : "#f0f7ff",
      borderRadius: 8,
      marginBottom: 4,
      borderLeft: `3px solid ${isCustomer ? "#52c41a" : isAdmin ? "#faad14" : "#91caff"}`,
    }}>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>
        <strong style={{ color: isCustomer ? "#389e0d" : isAdmin ? "#d48806" : "#014c75" }}>
          {isCustomer ? "Customer" : isAdmin ? "Admin" : "You"}
        </strong>
        {" · "}<Tag style={{ fontSize: 10, borderRadius: 20, padding: "0 6px" }}>
          {r.responseType?.replace(/_/g, " ")}
        </Tag>
        {" · "}{new Date(r.createdAt).toLocaleDateString("en-IN")}
      </div>
      <div style={{ fontSize: 12, color: "#444", lineHeight: 1.6 }}>{r.content}</div>
    </div>
  );
};

const FeedbackCard = ({ f, onRespond, submittingId }) => {
  const [replyOpen, setReplyOpen] = useState(false);
  const [text, setText] = useState("");

  const handleSend = async (type) => {
    const content = type === "acknowledgment" ? "Acknowledged." : text.trim();
    if (!content) return;
    await onRespond(f.id, { responseType: type, content });
    setText("");
    setReplyOpen(false);
  };

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e8eef5",
      borderRadius: 10,
      padding: "12px 16px",
      marginBottom: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Tag color={TYPE_COLOR[f.feedbackType]} style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
            {feedbackTypeLabel(f)}
          </Tag>
          {f.instantSubType && (
            <Tag color={SUBTYPE_COLOR[f.instantSubType]} style={{ borderRadius: 20, fontSize: 11 }}>
              {SUBTYPE_LABEL[f.instantSubType]}
            </Tag>
          )}
          {f.severity && (
            <Tag
              color={f.severity === "high" ? "red" : f.severity === "medium" ? "orange" : "default"}
              style={{ borderRadius: 20, fontSize: 11 }}
            >
              {f.severity}
            </Tag>
          )}
          {statusTag(f.status)}
        </div>
        <span style={{ fontSize: 11, color: "#bbb", whiteSpace: "nowrap" }}>
          {new Date(f.createdAt).toLocaleDateString("en-IN")}
        </span>
      </div>

      {f.title && <div style={{ fontWeight: 600, fontSize: 13, marginTop: 8, color: "#222" }}>{f.title}</div>}
      {(f.description || f.comments) && (
        <div style={{ fontSize: 12, color: "#555", marginTop: 4, lineHeight: 1.7 }}>
          {f.description || f.comments}
        </div>
      )}

      {f.ratingBucket && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11 }}>
            {f.ratingBucket.replace(/_/g, " ")}
          </Tag>
          {f.overallScore !== null && f.overallScore !== undefined && (
            <span style={{ fontWeight: 700, color: scoreColor(f.overallScore), fontSize: 14 }}>
              {f.overallScore}%
            </span>
          )}
        </div>
      )}

      {(f.coreStrengths || f.areasOfImprovement || f.keyAchievements) && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #eee", fontSize: 12, color: "#666" }}>
          {f.coreStrengths && <div style={{ marginBottom: 2 }}><strong style={{ color: "#444" }}>Strengths:</strong> {f.coreStrengths}</div>}
          {f.areasOfImprovement && <div style={{ marginBottom: 2 }}><strong style={{ color: "#444" }}>To Improve:</strong> {f.areasOfImprovement}</div>}
          {f.keyAchievements && <div><strong style={{ color: "#444" }}>Achievements:</strong> {f.keyAchievements}</div>}
        </div>
      )}

      {f.responses && f.responses.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
            <MessageOutlined style={{ fontSize: 11 }} />
            <strong>{f.responses.length}</strong> response{f.responses.length > 1 ? "s" : ""}
          </div>
          {f.responses.map(r => <ResponseBubble key={r.id} r={r} />)}
        </div>
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {f.status !== "closed" && (
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            loading={submittingId === f.id + "_ack"}
            onClick={() => handleSend("acknowledgment")}
            style={{ fontSize: 11, color: "#014c75", borderColor: "#b3cfe0" }}
          >
            Acknowledge
          </Button>
        )}
        <Button
          size="small"
          icon={<QuestionCircleOutlined />}
          onClick={() => setReplyOpen(p => !p)}
          style={{ fontSize: 11, color: "#014c75", borderColor: "#b3cfe0" }}
        >
          {replyOpen ? "Cancel" : "Raise Query"}
        </Button>
      </div>

      {replyOpen && (
        <div style={{ marginTop: 8 }}>
          <TextArea
            rows={2}
            placeholder="Describe your query or request for clarification…"
            value={text}
            onChange={e => setText(e.target.value)}
            style={{ borderRadius: 8, fontSize: 12, resize: "none" }}
            maxLength={1000}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              loading={submittingId === f.id + "_query"}
              disabled={!text.trim()}
              onClick={() => handleSend("query")}
              style={{ background: "#014c75", borderColor: "#014c75", fontSize: 11 }}
            >
              Send Query
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const PartnerTalentPerformanceDetail = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [talent, setTalent] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contracts");
  const [submittingId, setSubmittingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const token = () => localStorage.getItem("authToken");

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_CONST.PERFORMANCE_FEEDBACK_EXPORT}/${talentId}`, {
        headers: { Authorization: `Bearer ${token()}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      a.download = cd.split("filename=")[1]?.replace(/"/g, "") || `Performance_Report_${talentId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, feedbackRes] = await Promise.all([
        axios.get(`${API_CONST.GET_TALENT_PROFILE}/${talentId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
        axios.get(`${API_CONST.PERFORMANCE_FEEDBACK_TALENT}/${talentId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
      ]);
      setTalent(profileRes.data.talent || profileRes.data);
      setFeedback(feedbackRes.data.feedback || []);
    } catch {
      message.error("Failed to load talent performance data");
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRespond = async (feedbackId, data) => {
    const key = feedbackId + "_" + data.responseType;
    setSubmittingId(key);
    try {
      await axios.post(
        `${API_CONST.PERFORMANCE_FEEDBACK}/${feedbackId}/response`,
        data,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      message.success(data.responseType === "acknowledgment" ? "Acknowledged" : "Query sent");
      fetchData();
    } catch {
      message.error("Failed to submit response");
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 80 }}><Spin size="large" /></div>;
  }

  const contractMap = {};
  for (const f of feedback) {
    const key = f.hiringRecordId || "__no_contract";
    if (!contractMap[key]) {
      contractMap[key] = {
        hiringRecordId: key,
        hiringRecord: f.hiringRecord || null,
        customerOrganization: f.customerOrganization || "—",
        partnerOrganization: f.partnerOrganization || "—",
        items: [],
      };
    }
    contractMap[key].items.push(f);
  }
  const contracts = Object.values(contractMap).sort((a, b) => {
    const aDate = a.hiringRecord?.onboardingDate || "0";
    const bDate = b.hiringRecord?.onboardingDate || "0";
    return bDate.localeCompare(aDate);
  });

  const totalFeedback = feedback.length;
  const totalContracts = contracts.length;
  const scored = feedback.filter(f => f.overallScore !== null && f.overallScore !== undefined);
  const avgScore = scored.length > 0
    ? Math.round(scored.reduce((s, f) => s + f.overallScore, 0) / scored.length)
    : null;
  const openIssues = feedback.filter(f => f.status === "open").length;
  const recognitions = feedback.filter(f => f.instantSubType === "behavioral_recognition").length;

  const primarySkills = Array.isArray(talent?.primarySkills)
    ? talent.primarySkills
    : talent?.primarySkills ? [talent.primarySkills] : [];

  return (
    <Wrapper>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            style={{ paddingLeft: 0, color: "#014c75", fontWeight: 500 }}
            onClick={() => navigate("/partner/talent-performance")}
          >
            Back to Talent Performance
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
            style={{ borderRadius: 8 }}
          >
            Export Report
          </Button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Avatar
            size={56}
            icon={<UserOutlined />}
            style={{ background: "linear-gradient(135deg,#014c75,#0288c7)", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#014c75", lineHeight: 1.3 }}>
              {talent?.name || talentId}
            </div>
            <div style={{ fontSize: 13, color: "#999", marginTop: 2 }}>
              {talent?.role}
              {talent?.experienceYears ? ` · ${talent.experienceYears} yrs exp` : ""}
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {primarySkills.slice(0, 4).map(s => (
                <Tag key={s} color="blue" style={{ borderRadius: 20, fontSize: 11 }}>{s}</Tag>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { label: "Total Contracts", value: totalContracts, color: "#014c75", bg: "#f0f7ff", icon: <TeamOutlined /> },
          { label: "Total Feedback", value: totalFeedback, color: "#1677ff", bg: "#e6f4ff", icon: <FileTextOutlined /> },
          { label: "Lifetime Avg Score", value: avgScore !== null ? `${avgScore}%` : "N/A", color: scoreColor(avgScore), bg: "#f9f0ff", icon: <StarFilled /> },
          { label: "Open Issues", value: openIssues, color: openIssues > 0 ? "#ff4d4f" : "#52c41a", bg: openIssues > 0 ? "#fff2f0" : "#f6ffed", icon: <ExclamationCircleOutlined /> },
          { label: "Recognitions", value: recognitions, color: "#fa8c16", bg: "#fffbe6", icon: <CheckCircleOutlined /> },
        ].map((s, i) => (
          <Col xs={12} sm={8} md={4} lg={4} xl={4} key={i} style={{ flex: "1 1 160px" }}>
            <Card
              bordered={false}
              bodyStyle={{ padding: "14px 16px" }}
              style={{ borderRadius: 12, background: s.bg, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
            >
              <div style={{ fontSize: 11, color: "#888", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16, opacity: 0.7 }}>{s.icon}</span>
                {s.value}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ display: "flex", gap: 2, marginBottom: 16, borderBottom: "2px solid #f0f0f0" }}>
        {[
          { key: "contracts", label: "By Contract" },
          { key: "all", label: `All Feedback (${totalFeedback})` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              border: "none", background: "none", cursor: "pointer",
              padding: "10px 20px", fontSize: 14, fontWeight: activeTab === t.key ? 700 : 400,
              color: activeTab === t.key ? "#014c75" : "#888",
              borderBottom: activeTab === t.key ? "2px solid #014c75" : "2px solid transparent",
              marginBottom: -2, transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "contracts" && (
        contracts.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No contracts found" style={{ padding: 48 }} />
        ) : (
          <Collapse
            className="contract-panel"
            defaultActiveKey={contracts[0]?.hiringRecordId}
            style={{ background: "transparent", border: "none" }}
          >
            {contracts.map(contract => {
              const items = contract.items;
              const contractScored = items.filter(f => f.overallScore !== null && f.overallScore !== undefined);
              const contractAvg = contractScored.length > 0
                ? Math.round(contractScored.reduce((s, f) => s + f.overallScore, 0) / contractScored.length)
                : null;
              const contractOpen = items.filter(f => f.status === "open").length;
              const hr = contract.hiringRecord;
              const contractStatus = CONTRACT_STATUS[hr?.status] || { label: hr?.status || "—", color: "default" };

              return (
                <Panel
                  key={contract.hiringRecordId}
                  style={{ marginBottom: 12, border: "1px solid #e8eef5", borderRadius: "10px !important", overflow: "hidden", background: "#fff" }}
                  header={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#014c75" }}>
                          {contract.customerOrganization}
                        </div>
                        <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                          {hr?.onboardingDate ? `Since ${new Date(hr.onboardingDate).toLocaleDateString("en-IN")}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <Tag color={contractStatus.color} style={{ borderRadius: 20, fontSize: 11 }}>
                          {contractStatus.label}
                        </Tag>
                        <Tag style={{ borderRadius: 20, fontSize: 11, background: "#f0f7ff", borderColor: "#91caff", color: "#1677ff" }}>
                          {items.length} feedback
                        </Tag>
                        {contractOpen > 0 && (
                          <Tag color="error" style={{ borderRadius: 20, fontSize: 11 }}>
                            {contractOpen} open
                          </Tag>
                        )}
                        {contractAvg !== null && (
                          <Progress
                            type="circle"
                            percent={contractAvg}
                            size={32}
                            strokeWidth={8}
                            strokeColor={scoreColor(contractAvg)}
                            trailColor="#f0f0f0"
                            format={p => <span style={{ fontSize: 8, fontWeight: 700, color: scoreColor(contractAvg) }}>{p}%</span>}
                          />
                        )}
                      </div>
                    </div>
                  }
                >
                  {items.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No feedback for this contract" />
                  ) : (
                    <Timeline
                      style={{ paddingTop: 8 }}
                      items={items.map(f => ({
                        color: TYPE_COLOR[f.feedbackType] || "blue",
                        children: <FeedbackCard f={f} onRespond={handleRespond} submittingId={submittingId} />,
                      }))}
                    />
                  )}
                </Panel>
              );
            })}
          </Collapse>
        )
      )}

      {activeTab === "all" && (
        feedback.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No feedback on record" style={{ padding: 48 }} />
        ) : (
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: "20px 24px" }}>
            <Timeline
              items={[...feedback].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(f => ({
                color: TYPE_COLOR[f.feedbackType] || "blue",
                children: (
                  <div>
                    <div style={{ fontSize: 11, color: "#bbb", marginBottom: 4 }}>
                      <span style={{ fontWeight: 500, color: "#888" }}>{f.customerOrganization}</span>
                      {f.hiringRecord?.onboardingDate
                        ? ` · Contract since ${new Date(f.hiringRecord.onboardingDate).toLocaleDateString("en-IN")}`
                        : ""}
                    </div>
                    <FeedbackCard f={f} onRespond={handleRespond} submittingId={submittingId} />
                  </div>
                ),
              }))}
            />
          </Card>
        )
      )}
    </Wrapper>
  );
};

export default PartnerTalentPerformanceDetail;
